import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, Banknote, Copy, Check, X, ChevronLeft, ChevronRight, Star, Share2 } from "lucide-react";
import { resolveImageUrl, PLACEHOLDER } from "@/utils/imageUrl";
import { trackProductClick } from "@/utils/analytics";
import { buildProductMessage, openWhatsApp, getProductSizes } from "@/utils/whatsapp";
import { shareProduct } from "@/utils/share";
import { ProductModal } from "@/components/ProductModal";

const CLABE_INFO = {
  clabe: "014855606321190302",
  name: "Misael Jesus Navarro Cruz",
  bank: "Santander",
};


export const ProductCard = ({ product, autoOpen = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [showDetail, setShowDetail] = useState(autoOpen);

  useEffect(() => {
    if (autoOpen) trackProductClick(product.id, "view");
  }, [autoOpen, product.id]);

  const openDetail = () => {
    trackProductClick(product.id, "view");
    setShowDetail(true);
  };

  // Build image list: resolve all URLs
  const allImages = [];
  if (product.image_url) allImages.push(resolveImageUrl(product.image_url));
  if (product.images?.length) {
    product.images.forEach(img => {
      const resolved = resolveImageUrl(img);
      if (resolved && !allImages.includes(resolved)) allImages.push(resolved);
    });
  }
  if (allImages.length === 0) allImages.push(PLACEHOLDER);

  const sizes = getProductSizes(product);

  const savings = product.retail_price && product.retail_price > product.sale_price
    ? product.retail_price - product.sale_price
    : null;

  const handleWhatsAppClick = () => {
    trackProductClick(product.id, "whatsapp");
    const imageUrl = allImages[0] !== PLACEHOLDER ? allImages[0] : "";
    openWhatsApp(buildProductMessage(product, { imageUrl, selectedSize }));
  };

  const handleCopyCLABE = async () => {
    try {
      await navigator.clipboard.writeText(CLABE_INFO.clabe);
    } catch {
      const input = document.createElement('input');
      input.value = CLABE_INFO.clabe;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const prevImg = (e) => {
    e.stopPropagation();
    setCurrentImg(prev => (prev === 0 ? allImages.length - 1 : prev - 1));
  };
  const nextImg = (e) => {
    e.stopPropagation();
    setCurrentImg(prev => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="product-card" data-testid={`product-card-${product.id}`}>
      {/* Image Gallery */}
      <div className="relative aspect-[3/4] bg-[#0a0a0a] overflow-hidden rounded-t-2xl group/img cursor-pointer" onClick={openDetail} data-testid={`product-open-${product.id}`}>
        {!imageLoaded && (
          <div className="absolute inset-0 bg-[#121212] animate-pulse" />
        )}
        <img
          src={allImages[currentImg]}
          alt={product.name}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.target.src = PLACEHOLDER;
            setImageLoaded(true);
          }}
          className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* Gallery Nav - only if multiple images */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={prevImg}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
              data-testid={`img-prev-${product.id}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextImg}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
              data-testid={`img-next-${product.id}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {allImages.map((_, idx) => (
                <button
                  key={`dot-${idx}`}
                  onClick={(e) => { e.stopPropagation(); setCurrentImg(idx); }}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentImg ? 'bg-[#00FF66] w-4' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        )}

        {savings && (
          <div className="absolute top-3 left-3 bg-[#00FF66] text-[#050505] text-xs font-bold px-2 py-1 rounded-full" data-testid={`savings-badge-${product.id}`}>
            Ahorras ${savings.toFixed(0)}
          </div>
        )}
        {product.exclusive && (
          <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-[#050505] text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1" data-testid={`exclusive-badge-${product.id}`}>
            <Star className="w-3 h-3 fill-current" />
            Exclusivo
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); shareProduct(product); }}
          className={`absolute ${product.exclusive ? "bottom-3" : "top-3"} right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-[#B026FF] transition-colors`}
          title="Compartir con un amigo"
          aria-label="Compartir producto"
          data-testid={`share-btn-${product.id}`}
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>

      {/* Product Info */}
      <div className="p-4">
        {product.brand && (
          <p className="text-xs text-[#B026FF] uppercase tracking-wider mb-1 font-semibold">
            {product.brand}
          </p>
        )}

        <h3 className="font-medium text-sm text-white mb-2 line-clamp-2 min-h-[2.5rem] cursor-pointer hover:text-[#00FF66] transition-colors" onClick={openDetail} data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>

        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`sizes-${product.id}`}>
          {sizes.map((size, idx) => (
            <button
              key={`${size}-${idx}`}
              onClick={(e) => { e.stopPropagation(); setSelectedSize(selectedSize === size ? "" : size); }}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all duration-200 ${selectedSize === size ? "bg-[#00FF66] text-[#050505] border-[#00FF66] font-bold" : "bg-white/5 border-white/10 text-[#A0A0A0] hover:border-white/40 hover:text-white"}`}
              title="Elige tu talla"
              data-testid={`size-pick-${product.id}-${size}`}
            >
              {size}
            </button>
          ))}
        </div>

        <div className="mb-4">
          {product.retail_price > 0 && product.retail_price > product.sale_price && (
            <span className="text-xs text-[#A0A0A0] line-through mr-2" data-testid={`retail-price-${product.id}`}>
              ${product.retail_price.toFixed(0)}
            </span>
          )}
          <span className="text-xl font-oswald font-bold text-[#00FF66]">
            ${product.sale_price.toFixed(0)}
          </span>
          <span className="text-xs text-[#A0A0A0] ml-1">MXN</span>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={handleWhatsAppClick}
            className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-2.5 font-semibold text-xs uppercase tracking-wide flex items-center justify-center gap-2 rounded-full transition-all duration-300 hover:shadow-[0_0_12px_rgba(37,211,102,0.4)]"
            data-testid={`whatsapp-btn-${product.id}`}
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </button>
          <button
            onClick={() => { setShowPayment(true); trackProductClick(product.id, "payment"); }}
            className="w-full bg-transparent border border-[#B026FF] text-[#B026FF] hover:bg-[#B026FF] hover:text-white py-2.5 font-semibold text-xs uppercase tracking-wide flex items-center justify-center gap-2 rounded-full transition-all duration-300 hover:shadow-[0_0_12px_rgba(176,38,255,0.4)]"
            data-testid={`payment-btn-${product.id}`}
          >
            <Banknote className="w-4 h-4" />
            Apartar
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <ProductModal
          product={product}
          images={allImages}
          sizes={sizes}
          selectedSize={selectedSize}
          onSelectSize={setSelectedSize}
          onWhatsApp={handleWhatsAppClick}
          onApartar={() => { setShowDetail(false); setShowPayment(true); trackProductClick(product.id, "payment"); }}
          onClose={() => setShowDetail(false)}
        />
      )}

      {/* Payment Modal */}
      {showPayment && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[95] flex items-center justify-center p-4" onClick={() => setShowPayment(false)}>
          <div className="glass-card w-full max-w-sm p-0 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="font-oswald font-semibold text-white text-lg">Datos de Pago</h3>
              <button onClick={() => setShowPayment(false)} className="text-[#A0A0A0] hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-[#050505] rounded-xl p-4 text-center border border-white/5">
                <p className="text-xs text-[#A0A0A0] mb-1">Producto</p>
                <p className="text-white font-medium text-sm">{product.name}</p>
                <p className="text-[#00FF66] font-oswald font-bold text-2xl mt-1">${product.sale_price.toFixed(0)} MXN</p>
              </div>

              <div>
                <p className="text-xs text-[#A0A0A0] mb-1">Banco</p>
                <p className="text-white font-medium">{CLABE_INFO.bank}</p>
              </div>
              <div>
                <p className="text-xs text-[#A0A0A0] mb-1">Beneficiario</p>
                <p className="text-white font-medium">{CLABE_INFO.name}</p>
              </div>
              <div>
                <p className="text-xs text-[#A0A0A0] mb-1">CLABE</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#050505] border border-white/10 px-3 py-2 text-[#00FF66] font-mono text-sm tracking-wider rounded-lg" data-testid="clabe-number">
                    {CLABE_INFO.clabe}
                  </code>
                  <button
                    onClick={handleCopyCLABE}
                    className={`p-2 rounded-lg border transition-all duration-300 ${copied ? 'border-[#00FF66] text-[#00FF66]' : 'border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30'}`}
                    data-testid="copy-clabe-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <p className="text-xs text-[#A0A0A0] text-center">
                Envia tu comprobante por WhatsApp para confirmar
              </p>

              <button
                onClick={() => { setShowPayment(false); handleWhatsAppClick(); }}
                className="w-full bg-[#25D366] text-white py-3 font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2 rounded-full transition-all duration-300 hover:shadow-[0_0_16px_rgba(37,211,102,0.4)]"
                data-testid="send-receipt-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Enviar Comprobante
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
